import type { FC } from 'react';
import { motion, type Variants } from 'framer-motion';

import type { SanityImageGrid } from '#/lib/sanity/queries/imageGrid';
import {
  getSectionButtonTheme,
  getSectionThemeAccent,
  getSectionThemeSurface,
  isDarkSectionTheme,
  normalizeSectionTheme,
} from '#/lib/section-theme';
import { cn } from '#/lib/utils';
import { Button } from '#/components/Button';
import { formatHeadline } from '#/components/Headline';
import { Media } from '#/components/Media';

const REVEAL_VIEWPORT = { once: true, margin: '-100px' } as const;

const introGroup: Variants = {
  hidden: {},
  show: { transition: { delayChildren: 0.05, staggerChildren: 0.1 } },
};

const cardsGroup: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12 } },
};

const fadeIn: Variants = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.33, 1, 0.68, 1] },
  },
};

export const ImageGrid: FC<SanityImageGrid> = (props) => {
  const theme = normalizeSectionTheme(props.theme, 'charcoal');
  const isDark = isDarkSectionTheme(theme);
  const accentClass = getSectionThemeAccent(theme);
  const buttonTheme = getSectionButtonTheme(theme);
  const hasIntro =
    props.eyebrow || props.title || props.description || props.cta?.href;

  return (
    <div
      className={cn(
        'p-main py-40 m:py-80 l:py-100',
        getSectionThemeSurface(theme),
      )}
    >
      <div className="mx-auto flex max-w-1235 flex-col items-center gap-40 m:gap-60">
        {hasIntro ? (
          <motion.div
            className="flex w-full max-w-800 flex-col items-center gap-20 text-center m:gap-48"
            variants={introGroup}
            initial="hidden"
            whileInView="show"
            viewport={REVEAL_VIEWPORT}
          >
            {props.eyebrow ? (
              <motion.p
                variants={fadeIn}
                className={cn('type-eyebrow', accentClass)}
              >
                {props.eyebrow}
              </motion.p>
            ) : null}

            {(props.title || props.description) && (
              <div className="flex w-full flex-col items-center gap-20">
                {props.title ? (
                  <motion.h2
                    variants={fadeIn}
                    className="type-heading5 m:type-heading3 text-balance"
                  >
                    {formatHeadline(props.title, accentClass)}
                  </motion.h2>
                ) : null}
                {props.description ? (
                  <motion.p
                    variants={fadeIn}
                    className={cn(
                      'type-paragraph',
                      isDark ? 'text-grayscale-03' : 'text-grayscale-05',
                    )}
                  >
                    {props.description}
                  </motion.p>
                ) : null}
              </div>
            )}

            {props.cta?.href ? (
              <motion.div variants={fadeIn}>
                <Button
                  href={props.cta.href}
                  target={props.cta.target}
                  variant="primary"
                  theme={buttonTheme}
                >
                  {props.cta.title}
                </Button>
              </motion.div>
            ) : null}
          </motion.div>
        ) : null}

        <motion.div
          className="grid w-full grid-cols-1 gap-20 m:grid-cols-2 m:gap-24"
          variants={cardsGroup}
          initial="hidden"
          whileInView="show"
          viewport={REVEAL_VIEWPORT}
        >
          {props.items.map((item) => (
            <motion.article
              key={item._key}
              variants={fadeIn}
              className={cn(
                'flex h-full flex-col overflow-hidden rounded-16',
                isDark ? 'bg-grayscale-06' : 'bg-cloud',
              )}
            >
              <Media
                media={item.media}
                className="aspect-[628/512] w-full rounded-none"
                mediaClassName="rounded-none object-cover"
                aspect={false}
                sizes={{ m: '50vw', '1285px': '605px' }}
              />
              <div className="flex flex-1 flex-col gap-12 px-20 pt-24 m:px-40 m:pt-40">
                <h3
                  className={cn(
                    'type-heading5 text-18 m:text-24',
                    isDark ? 'text-cloud' : 'text-charcoal',
                  )}
                >
                  {item.title}
                </h3>
                <p
                  className={cn(
                    'type-paragraph',
                    isDark ? 'text-grayscale-03' : 'text-grayscale-05',
                  )}
                >
                  {item.description}
                </p>
              </div>
              {item.link?.href ? (
                <div className="px-20 py-16 m:px-40">
                  <Button
                    href={item.link.href}
                    target={item.link.target}
                    variant="link"
                    theme={buttonTheme}
                  >
                    {item.link.title}
                  </Button>
                </div>
              ) : (
                <div className="pb-24 m:pb-40" />
              )}
            </motion.article>
          ))}
        </motion.div>
      </div>
    </div>
  );
};
